import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';

import { forwardRef, useMemo } from 'react';

import {
    Box,
    Button,
    Flex,
    Heading,
    Link,
    Separator,
    Text,
    useBreakpointValue,
    useChakraContext,
} from '@chakra-ui/react';

import isNil from 'lodash/isNil';

import { AdaptiveDrawer } from '@nocowanie/common-ui/components/molecules/adaptive-drawer';
import { IconsLinear } from '@nocowanie/icons/linear';

import { DistanceFrom } from '@app/components/atoms/distance-from';
import { LeafletMapProps } from '@app/components/molecules/leaflet-map';
import { AppModalsEnum, DistanceTargetType } from '@app/enums';
import { useDrawerState } from '@app/hooks/useDrawerState';
import { useModalFragmentSync } from '@app/hooks/useModalFragmentSync';
import * as accommodationStaticSlice from '@app/state/slices/accommodation-static.slice';
import * as commonUiStateSlice from '@app/state/slices/common-ui-state.slice';
import { useAppDispatch, useAppSelector } from '@app/state/state';

import { LocationProps } from './location.props';

const LeafletMap = dynamic(() => import('@app/components/molecules/leaflet-map/leaflet-map'), {
    ssr: false,
});

const Location = forwardRef<HTMLDivElement, any>((props: LocationProps, ref) => {
    const theme = useChakraContext();
    const fontSizeSm = theme.tokens.getByName('fontSizes.sm')?.value;

    const { t } = useTranslation('accommodation', {
        keyPrefix: 'location',
    });
    const isMobileBrowser = useAppSelector(commonUiStateSlice.selectors.selectIsMobileVersion);
    const isMobileRes = useBreakpointValue(
        {
            base: true,
            md: false,
        },
        {
            fallback: isMobileBrowser ? 'base' : 'md',
        },
    );
    const dispatch = useAppDispatch();
    const isDrawerOpen = useAppSelector(
        commonUiStateSlice.selectors.selectModalData<boolean>(AppModalsEnum.Location),
    );
    const { latitude, longitude, name, address, streetNumber, settlementName } = useAppSelector(
        accommodationStaticSlice.selectors.selectLocationData,
    );
    const cityLocation = useAppSelector(accommodationStaticSlice.selectors.selectCityLocation);

    const { isDrawerPending, openDrawer } = useDrawerState(() => {
        dispatch(
            commonUiStateSlice.actions.setModalDataAction({
                modalName: AppModalsEnum.Location,
                modalData: true,
            }),
        );
    });

    const closeDrawer = () => {
        dispatch(commonUiStateSlice.actions.resetModalDataAction(AppModalsEnum.Location));
    };

    useModalFragmentSync(AppModalsEnum.Location, isDrawerOpen);

    const displayedAddress = useMemo(
        () =>
            settlementName
                ? address && streetNumber
                    ? `${address} ${streetNumber}, ${settlementName}`
                    : settlementName
                : undefined,
        [address, streetNumber, settlementName],
    );

    if (isNil(latitude) || isNil(longitude)) return null;

    const commonMapParams = {
        center: { lat: latitude, lng: longitude },
        zoom: 13,
        attributionControl: false,
    } as LeafletMapProps;

    return (
        <>
            <Heading size={'md'} mb={2} ref={ref}>
                {t('locationHeader')}
            </Heading>
            <Flex flexDirection={{ base: 'row', md: 'row-reverse' }} gap={{ base: '4', sm: '6' }}>
                <Box
                    flexGrow={1}
                    minW="128px"
                    minH="96px"
                    h={{ base: 'auto', sm: '160px', md: '220px' }}
                    cursor={'pointer'}
                    borderRadius={5}
                    overflow={'hidden'}
                    data-testid="location-map"
                >
                    <LeafletMap
                        onMapClick={openDrawer}
                        dragging={false}
                        keyboard={false}
                        touchZoom={false}
                        zoomControl={false}
                        doubleClickZoom={false}
                        scrollWheelZoom={false}
                        {...commonMapParams}
                    />
                </Box>
                <Flex
                    flexDirection={'column'}
                    justifyContent={'space-between'}
                    flexShrink={{ sm: '0' }}
                    gap={4}
                    maxW={240}
                    data-testid="location-address"
                >
                    {displayedAddress ? (
                        <Flex flexDirection={'row'} gap={1}>
                            <IconsLinear.Location size={isMobileRes ? 'xs' : 'sm'} />
                            <Flex flexDirection={'column'}>
                                <Link
                                    onClick={openDrawer}
                                    variant="plain"
                                    fontSize={{ base: 'sm', sm: 'md' }}
                                    textAlign="left"
                                    whiteSpace="normal"
                                >
                                    {displayedAddress}
                                </Link>

                                {cityLocation ? (
                                    <DistanceFrom
                                        accommodationCoords={{
                                            lat: latitude,
                                            lon: longitude,
                                        }}
                                        targetCoords={cityLocation}
                                        targetType={DistanceTargetType.CityCenter}
                                        styles={{
                                            fontSize: fontSizeSm,
                                        }}
                                    ></DistanceFrom>
                                ) : null}
                            </Flex>
                        </Flex>
                    ) : null}

                    {/* TODO :: Due to the latest findings (DNI-1637), NearbyPlaces component must be hidden for now  */}
                    {/* <NearbyPlaces /> */}
                </Flex>
            </Flex>

            {isMobileRes ? (
                <Flex direction={'column'} mt={5}>
                    <Button
                        onClick={openDrawer}
                        colorPalette={'secondary'}
                        loading={isDrawerPending}
                        disabled={isDrawerPending}
                    >
                        {t('showOnMap')}
                    </Button>
                </Flex>
            ) : null}

            <AdaptiveDrawer
                isOpen={isDrawerOpen}
                onOpen={openDrawer}
                onClose={closeDrawer}
                header={t('locationHeader')}
                display={'flex'}
                flexDirection={'column'}
                p={0}
                drawerProps={{
                    variant: isMobileRes ? 'withMargin' : undefined,
                    placement: isMobileRes ? 'bottom' : 'end',
                    size: isMobileRes ? 'full' : 'lg',
                }}
            >
                <Text fontSize={'md'} fontWeight={'bold'} px={5} py={3}>
                    {name}
                </Text>
                <LeafletMap name={name} displayedAddress={displayedAddress} {...commonMapParams} />
            </AdaptiveDrawer>

            <Separator orientation="horizontal" mt={6} />
        </>
    );
});

Location.displayName = 'Location';
export { Location };
